import React, { useState } from "react";
import "./adminStock.scss";
import {
  useAddVariantStockIncrementMutation,
  useGetProductsQuery,
} from "../../features/api/ProductApiSlice";
import FuelLoading from "../../pages/FuelLoading";
import { useToast } from "../toast/ToastContext";
import AdminHeader from "../layout/AdminHeader";

export default function AdminStockManager() {
  const { showToast } = useToast();
  const [selectedProductId, setSelectedProductId] = useState("");
  const [adjustmentAmounts, setAdjustmentAmounts] = useState({});

  const {
    data: catalogData,
    isLoading: isLoadingCatalog,
    isError,
  } = useGetProductsQuery({ adminMode: true });
  const [adjustStock, { isLoading: isSubmittingBump }] =
    useAddVariantStockIncrementMutation();

  const allProducts = catalogData?.items || catalogData || [];

  // Only products whose inventory Mini Miles manages directly.
  // Printify manages its own production and availability.
  const productsList = allProducts.filter(
    (product) => product.fulfillmentMethod !== "printify"
  );
  const activeProduct = productsList.find((p) => p._id === selectedProductId);

  const handleInputChange = (variantId, val) => {
    setAdjustmentAmounts((prev) => ({ ...prev, [variantId]: val }));
  };

  // Modern Single submission route dispatcher handling direction payloads
  const handleStockAdjustment = async (e, variantId, direction) => {
    e.preventDefault();
    const rawInput = parseInt(adjustmentAmounts[variantId], 10);

    if (!rawInput || rawInput <= 0) {
      showToast(
        "Please declare a valid numerical quantity greater than zero.",
        "error"
      );
      return;
    }

    // Convert value to negative if executing a damaged item log write-off
    const finalAmount = direction === "add" ? rawInput : rawInput * -1;

    try {
      const response = await adjustStock({
        productId: selectedProductId,
        variantId: variantId,
        amountToAdd: finalAmount,
      }).unwrap();

      showToast(response.message, "success");
      setAdjustmentAmounts((prev) => ({ ...prev, [variantId]: "" }));
    } catch (err) {
      const errorMsg =
        err.data?.message || err.error || "Warehouse operation fault.";
      showToast(errorMsg, "error");
    }
  };

  if (isLoadingCatalog) {
    return (
      <div className="admin-stock-loading-box">
        <FuelLoading text="Opening warehouse inventory ledger matrix..." />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="admin-stock-error">
        ⚠️ Secure terminal access denied or catalog repository is offline.
      </div>
    );
  }

  return (
    <div className="admin-stock-manager fuel-fade-in">
      {/* 🟢 CALL DYNAMICALLY WITH LOGISTICS CUSTOM VALUES */}
      <AdminHeader
        title="Logistics & Stock Control Deck"
        description="Monitor real-time warehouse variations metrics, replenish trending apparel parameters, or process damaged items write-offs securely."
        badgeText="Inventory Control"
      />
      {isSubmittingBump && (
        <FuelLoading
          text="Committing logistics logs to database..."
          fullScreen
        />
      )}

      <header className="dashboard-header">
        <h1>Mini Miles Logistics & Stock Deck</h1>
        <p>
          Restock branded merchandise variants or log write-offs for damaged
          gear items.
        </p>
      </header>

      {/* STEP 1: CUSTOM UN-BLUE THEMED WRAPPED DROPDOWN TUNNEL */}
      <div className="control-selector-row">
        <label htmlFor="product-selector-field">Target Product Profile:</label>
        <div className="custom-select-wrapper">
          <select
            id="product-selector-field"
            value={selectedProductId}
            onChange={(e) => {
              setSelectedProductId(e.target.value);
              setAdjustmentAmounts({});
            }}
          >
            <option value="">
              -- Choose Gear Inventory Item to Inspect --
            </option>
            {productsList.map((product) => (
              <option key={product._id} value={product._id}>
                {product.productType === "branded_merchandise"
                  ? "📦 Merch: "
                  : "🔗 Affiliate: "}{" "}
                {product.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* STEP 2: VARIANTS STOCK TRACKING GRID ROWS CONTAINER SHEET */}
      {activeProduct ? (
        <section className="stock-adjustment-zone">
          <div className="product-summary-meta">
            <img
              src={activeProduct.images?.[0]}
              alt=""
              className="summary-thumb"
            />
            <div className="summary-text">
              <h2>{activeProduct.name}</h2>
              <span
                className={`global-status-pill ${
                  activeProduct.inStock ? "status--instock" : "status--soldout"
                }`}
              >
                Global Status:{" "}
                {activeProduct.inStock
                  ? "🟢 Active Catalog View"
                  : "🔴 Flagged Out of Stock"}
              </span>
            </div>
          </div>

          <div className="admin-table-scroll-wrapper">
            <table className="admin-stock-table">
              <thead>
                <tr>
                  <th>Variant SKU Code</th>
                  <th>Specs (Size / Color / Vol)</th>
                  <th>Current Stock</th>
                  <th style={{ textAlign: "right" }}>
                    Warehouse Actions Configuration
                  </th>
                </tr>
              </thead>
              <tbody>
                {!activeProduct.variants ||
                activeProduct.variants.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="table-empty-row">
                      No distinct operational variations recorded for this
                      product model profile.
                    </td>
                  </tr>
                ) : (
                  activeProduct.variants.map((v) => (
                    <tr key={v._id}>
                      <td className="sku-cell">
                        <code>{v.sku}</code>
                      </td>

                      <td className="specs-cell">
                        <div className="chips-matrix">
                          {v.size && (
                            <span className="spec-chip">Size: {v.size}</span>
                          )}
                          {v.colorName && (
                            <span className="spec-chip">
                              Color: {v.colorName}
                            </span>
                          )}
                          {v.volume && (
                            <span className="spec-chip">Vol: {v.volume}</span>
                          )}
                        </div>
                      </td>

                      <td className="quantity-count-cell">
                        <strong
                          className={
                            v.countInStock < 5
                              ? "low-alert-text"
                              : "safe-count-text"
                          }
                        >
                          {v.countInStock} units
                        </strong>
                      </td>

                      {/* BUMP ACTIONS SUBMIT BOX WITH BOTH OPTIONS */}
                      <td style={{ textAlign: "right" }}>
                        <div className="quick-bump-actions-cluster">
                          <input
                            type="number"
                            min="1"
                            step="1"
                            placeholder="Count..."
                            value={adjustmentAmounts[v._id] || ""}
                            onChange={(e) =>
                              handleInputChange(v._id, e.target.value)
                            }
                          />
                          <button
                            type="button"
                            className="btn-stock btn-stock--add"
                            onClick={(e) =>
                              handleStockAdjustment(e, v._id, "add")
                            }
                            disabled={isSubmittingBump}
                          >
                            Replenish
                          </button>
                          <button
                            type="button"
                            className="btn-stock btn-stock--remove"
                            onClick={(e) =>
                              handleStockAdjustment(e, v._id, "remove")
                            }
                            disabled={isSubmittingBump}
                          >
                            Write-Off
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      ) : (
        <div className="empty-selection-slate">
          <span className="slate-icon">🚛</span>
          <p>
            Please select a specific gear file target track above to display
            stock matrix blocks.
          </p>
        </div>
      )}
    </div>
  );
}
